/** @typedef {import("../api/types").Me} Me */
import React, {useEffect, useState} from "react";
import axios from "axios";
import EmailLink from "../shared/components/EmailLink";
import {Link, NavLink as RouterNavLink, useLocation} from "react-router-dom";
import {
    Collapse,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    Navbar,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    NavLink,
    UncontrolledDropdown,
} from "reactstrap";
import IconLabel from "../shared/components/IconLabel";
import NotificationBell from "./NotificationBell";
import {ChartIcon, LogoutIcon, SlidersIcon, UserIcon} from "../shared/components/icons";
import {syncLook} from "../shared/utils/look";
import {syncTheme} from "../shared/utils/theme";
import {appVersion} from "../shared/utils/version";

const ME_URL = "/os/api/me/";
const CATALOG_PATHS = ["/service-items", "/products", "/device-types"];

const Header = () => {
    const [open, setOpen] = useState(false);
    const [me, setMe] = useState(/** @type {Me|null} */ (null));

    useEffect(() => {
        const controller = new AbortController();
        axios
            .get(ME_URL, {signal: controller.signal})
            .then(({data}) => {
                setMe(data);
                // what the provider saved is what the app wears (nothing saved: the default)
                if ("theme" in data) syncTheme(data.theme);
                if ("look" in data) syncLook(data.look);
            })
            .catch(() => {
                // Non-critical: the dropdown falls back to a generic label.
            });
        return () => controller.abort();
    }, []);

    const label = me?.username?.trim() || me?.email || "Perfil";
    const close = () => setOpen(false);
    const {pathname} = useLocation();
    const inCatalog = CATALOG_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
    const version = appVersion();

    return (
        <Navbar color="dark" dark expand="md" className="ordo-navbar">
            <Container className="d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between">
                <NavbarBrand tag={RouterNavLink} to="/" className="d-flex align-items-center fs-3 fw-semibold">
                    <img src="/favicon.svg" alt="" width="40" height="40" className="me-2" />
                    Ordo
                </NavbarBrand>
                <NavbarToggler onClick={() => setOpen(!open)} className="ms-auto" />
                <Collapse isOpen={open} navbar>
                    <Nav className="me-auto" navbar>
                        <NavItem>
                            {/* `end`: the front page is "/", which every address starts with */}
                            <NavLink tag={RouterNavLink} to="/" end onClick={close}>
                                Início
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink tag={RouterNavLink} to="/orders" onClick={close}>
                                Ordens de serviço
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink tag={RouterNavLink} to="/clients" onClick={close}>
                                Clientes
                            </NavLink>
                        </NavItem>
                        {/* what is set up once and picked from in orders, so it is one click deeper */}
                        <UncontrolledDropdown nav inNavbar>
                            <DropdownToggle nav caret className={inCatalog ? "active" : undefined}>
                                Catálogo
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem tag={RouterNavLink} to="/service-items" onClick={close}>
                                    Serviços
                                </DropdownItem>
                                <DropdownItem tag={RouterNavLink} to="/products" onClick={close}>
                                    Produtos
                                </DropdownItem>
                                <DropdownItem tag={RouterNavLink} to="/device-types" onClick={close}>
                                    Tipos de equipamento
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </Nav>
                    <Nav navbar className="align-items-md-center">
                        <NotificationBell onNavigate={close} />
                        <UncontrolledDropdown nav inNavbar>
                            <DropdownToggle nav caret>
                                <IconLabel icon={<UserIcon />}>{label}</IconLabel>
                            </DropdownToggle>
                            <DropdownMenu end>
                                {me && (
                                    <DropdownItem header className="text-wrap">
                                        {me.business_name}
                                        <div className="fw-normal">
                                            <EmailLink email={me.email} />
                                        </div>
                                    </DropdownItem>
                                )}
                                <DropdownItem tag={RouterNavLink} to="/accounting" onClick={close}>
                                    <IconLabel icon={<ChartIcon />}>Contabilidade</IconLabel>
                                </DropdownItem>
                                <DropdownItem tag={RouterNavLink} to="/config" onClick={close}>
                                    <IconLabel icon={<SlidersIcon />}>Configurações</IconLabel>
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem tag={RouterNavLink} to="/logout" onClick={close}>
                                    <IconLabel icon={<LogoutIcon />}>Sair</IconLabel>
                                </DropdownItem>
                                {version && (
                                    <>
                                        <DropdownItem divider />
                                        <DropdownItem
                                            tag={Link}
                                            to="/changelog"
                                            className="small text-muted"
                                            onClick={close}
                                        >
                                            Ordo v{version}
                                        </DropdownItem>
                                    </>
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </Nav>
                </Collapse>
            </Container>
        </Navbar>
    );
};

export default Header;
